import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { PageList, ENUM_FORM_DISPLAY } from "../../components/pageList";

const apiEntity = "emailSendLog";

const formSchema = {
  title: "Email Send Log",
  apiEntity,
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "lg" },
  initialValues: {},
  layout: { fields: [] },
};

const EmailSendLog = () => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);

  const tableSchema = useMemo(
    () => ({
      searchableFields: ["source", "sourceLabel", "to", "cc", "bcc", "subject", "error"],
      columns: [
        {
          field: "sentAt",
          headerName: "Sent At",
          width: 160,
          cellRender: (col, data) =>
            data?.sentAt ? moment(data.sentAt).format("YYYY-MM-DD HH:mm") : "",
        },
        { field: "source", headerName: "Source", width: 140, filter: true },
        { field: "sourceLabel", headerName: "Label", width: 160 },
        { field: "to", headerName: "To", width: 200 },
        { field: "subject", headerName: "Subject", width: 240 },
        { field: "cc", headerName: "CC", width: 140 },
        {
          field: "attachmentNames",
          headerName: "Attachments",
          width: 180,
          cellRender: (col, data) =>
            Array.isArray(data?.attachmentNames)
              ? data.attachmentNames.join(", ")
              : "",
        },
        {
          field: "status",
          headerName: "Status",
          width: 100,
          cellRender: (col, data) => (
            <Badge size="sm" color={data?.status === "SENT" ? "green" : "red"}>
              {data?.status}
            </Badge>
          ),
        },
        {
          field: "error",
          headerName: "Error",
          width: 180,
          cellRender: (col, data) => data?.error ?? "",
        },
        {
          field: "action",
          headerName: "",
          width: 90,
          cellRender: (col, data) => (
            <Text
              size="sm"
              color="blue"
              sx={{ cursor: "pointer" }}
              onClick={() => setDetail(data)}
            >
              {t("View")}
            </Text>
          ),
        },
      ],
    }),
    [t]
  );

  return (
    <>
      <PageList
        apiEntity={apiEntity}
        title={t("Email Send Log")}
        formSchema={formSchema}
        showAddButton={false}
        tableSchema={tableSchema}
        initSort={{ by: "sentAt", order: "desc" }}
      />

      <Modal
        opened={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.subject || t("Email detail")}
        size="lg"
        centered
      >
        {detail ? (
          <Stack spacing="sm">
            <Group spacing="xl">
              <Box>
                <Text size="xs" color="dimmed">
                  {t("Status")}
                </Text>
                <Badge color={detail.status === "SENT" ? "green" : "red"}>
                  {detail.status}
                </Badge>
              </Box>
              <Box>
                <Text size="xs" color="dimmed">
                  {t("Sent At")}
                </Text>
                <Text size="sm">
                  {detail.sentAt
                    ? moment(detail.sentAt).format("YYYY-MM-DD HH:mm:ss")
                    : ""}
                </Text>
              </Box>
              <Box>
                <Text size="xs" color="dimmed">
                  {t("Source")}
                </Text>
                <Text size="sm">{detail.source}</Text>
              </Box>
            </Group>
            <Text size="sm">
              <Text span weight={600}>
                {t("To")}:{" "}
              </Text>
              {detail.to}
            </Text>
            {detail.cc ? (
              <Text size="sm">
                <Text span weight={600}>
                  CC:{" "}
                </Text>
                {detail.cc}
              </Text>
            ) : null}
            {detail.bcc ? (
              <Text size="sm">
                <Text span weight={600}>
                  BCC:{" "}
                </Text>
                {detail.bcc}
              </Text>
            ) : null}
            {detail.error ? (
              <Text size="sm" color="red">
                {detail.error}
              </Text>
            ) : null}
            <Text size="xs" weight={600}>
              {t("Body")}
            </Text>
            <ScrollArea style={{ maxHeight: 360 }}>
              <Box
                p="sm"
                sx={(theme) => ({
                  border: `1px solid ${theme.colors.gray[3]}`,
                  borderRadius: theme.radius.sm,
                  fontSize: theme.fontSizes.sm,
                })}
                dangerouslySetInnerHTML={{ __html: detail.body || "" }}
              />
            </ScrollArea>
          </Stack>
        ) : null}
      </Modal>
    </>
  );
};

export default EmailSendLog;
